// @ts-nocheck

import { useState } from 'react';
import { createPortal } from 'react-dom';
import { OutsideClickHandler } from '../Search';
import RULERIcon from '../../assets/ruler.svg?react';

// Add CSS for spinner animation and modal overrides
const modalCSS = `
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* Force modal to appear above everything */
.alfinder-size-guide-modal {
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  bottom: 0 !important;
  z-index: 2147483647 !important; /* Maximum z-index value */
  pointer-events: auto !important;
}

.alfinder-size-guide-modal * {
  z-index: inherit !important;
}
`;

// Inject the CSS if not already present
if (!document.querySelector('#alfinder-size-guide-css')) {
  const style = document.createElement('style');
  style.id = 'alfinder-size-guide-css';
  style.textContent = modalCSS;
  document.head.appendChild(style);
}

interface SizeGuideProps {
  open?: boolean;
  primaryColor?: string;
  locale?: 'ar-SA' | 'en-US';
  buttonText?: string;
  modalTitle?: string;
  sizeData?: {
    id?: string;
    name?: string;
    description?: string;
    tableData:
      | {
          columns: Array<{
            name: string;
            value: string;
          }>;
          rows: Array<{
            name: string;
            values: string[];
          }>;
        }
      | Array<{
          columns: Array<{
            name: string;
            value: string;
          }>;
          rows: Array<{
            name: string;
            values: string[];
          }>;
        }>;
    storeId?: number;
    createdAt?: string;
    updatedAt?: string;
  };
  provider?: string;
}

const defaultSizeData = {
  id: 'default-size-guide',
  name: 'Standard Size Chart',
  description: 'Standard sizing measurements',
  tableData: {
    columns: [
      { name: 'Chest', value: 'cm' },
      { name: 'Waist', value: 'cm' },
      { name: 'Hips', value: 'cm' },
      { name: 'Length', value: 'cm' },
    ],
    rows: [
      { name: 'XS', values: ['86-89', '66-69', '91-94', '68'] },
      { name: 'S', values: ['91-94', '71-74', '96-99', '70'] },
      { name: 'M', values: ['96-99', '76-79', '101-104', '72'] },
      { name: 'L', values: ['101-104', '81-84', '106-109', '74'] },
      { name: 'XL', values: ['106-111', '86-91', '111-116', '76'] },
      { name: 'XXL', values: ['116-121', '96-101', '121-126', '78'] },
    ],
  },
};

const SizeGuide: React.FC<SizeGuideProps> = ({
  open = false,
  primaryColor = '#000000',
  locale = 'ar-SA',
  buttonText,
  modalTitle,
  sizeData,
}) => {
  const [openDialog, setOpenDialog] = useState(open);
  const [isClosing, setIsClosing] = useState(false);
  const [isConverted, setIsConverted] = useState(false);

  const isArabic = locale === 'ar-SA';

  // Unit conversion functions
  const cmToInches = (cm: number) => (cm / 2.54).toFixed(1);
  const inchesToCm = (inches: number) => (inches * 2.54).toFixed(1);

  // Convert measurement value
  const convertValue = (value: string, fromUnit: string, toUnit: string) => {
    if (!value || value === '-') return value;

    // Handle range values like "30-35"
    if (value.includes('-')) {
      const [min, max] = value.split('-');
      const convertedMin = convertSingleValue(min, fromUnit, toUnit);
      const convertedMax = convertSingleValue(max, fromUnit, toUnit);
      return `${convertedMin}-${convertedMax}`;
    }

    return convertSingleValue(value, fromUnit, toUnit);
  };

  const convertSingleValue = (
    value: string,
    fromUnit: string,
    toUnit: string,
  ) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return value;

    if (fromUnit.toLowerCase() === 'cm' && toUnit.toLowerCase() === 'in') {
      return cmToInches(numValue);
    } else if (
      fromUnit.toLowerCase() === 'in' &&
      toUnit.toLowerCase() === 'cm'
    ) {
      return inchesToCm(numValue);
    }

    return value;
  };

  // Transform size data (either from props or API) if it has tableData as array
  const transformSizeData = (data: any) => {
    if (!data) return null;
    return {
      ...data,
      tableData:
        Array.isArray(data.tableData) && data.tableData.length > 0
          ? data.tableData[0]
          : data.tableData,
    };
  };

  // Use sizeData prop or fallback to default data
  const transformedPropsData = transformSizeData(sizeData);

  const currentSizeData = transformedPropsData || defaultSizeData;

  const texts = {
    buttonText: buttonText || (isArabic ? 'دليل المقاسات' : 'Size Guide'),
    modalTitle:
      modalTitle ||
      currentSizeData?.name ||
      (isArabic ? 'دليل المقاسات' : 'Size Guide'),
    size: isArabic ? 'المقاس' : 'Size',
  };

  const handleOpenModal = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setOpenDialog(true);
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setOpenDialog(false);
      setIsClosing(false);
    }, 300);
  };

  // Get dynamic columns based on transposed structure (sizes as columns)
  const getTableColumns = () => {
    if (!currentSizeData?.tableData?.rows) {
      return [];
    }

    // First column is for measurement type (chest, legs, etc.)
    const columns = [{ key: 'measurement', label: isArabic ? 'مقاس' : 'Size' }];

    // Add size names as column headers
    currentSizeData.tableData.rows.forEach((row) => {
      columns.push({
        key: row.name,
        label: row.name,
      });
    });

    return columns;
  };

  // Get table rows (measurement types with their values)
  const getTableRows = () => {
    if (
      !currentSizeData?.tableData?.columns ||
      !currentSizeData?.tableData?.rows
    ) {
      return [];
    }

    // Create rows for each measurement type (chest, legs, etc.)
    return currentSizeData.tableData.columns.map((column, columnIndex) => {
      const originalUnit = column.value;
      const displayUnit = isConverted
        ? originalUnit.toLowerCase() === 'cm'
          ? 'in'
          : 'cm'
        : originalUnit;

      const row = {
        measurement: `${column.name} (${displayUnit})`,
      };

      // Add values for each size with conversion if needed
      currentSizeData.tableData.rows.forEach((sizeRow) => {
        const originalValue = sizeRow.values[columnIndex] || '-';
        const displayValue = isConverted
          ? convertValue(originalValue, originalUnit, displayUnit)
          : originalValue;
        row[sizeRow.name] = displayValue;
      });

      return row;
    });
  };

  const tableColumns = getTableColumns();
  const tableRows = getTableRows();

  return (
    <>
      {/* Size Guide Button */}
      <div style={{ display: 'flex', alignItems: 'center' }}>
        <button
          type="button"
          onClick={handleOpenModal}
          style={{
            color: '#FFFFFF',
            backgroundColor: primaryColor || '#000000',
            fontSize: '14px',
            fontWeight: '900',
            cursor: 'pointer',
            outline: 'none',
            fontFamily: 'inherit',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '5px',
            borderRadius: '20px',
            width: '200px',
          }}
        >
          <RULERIcon
            style={{ width: '30px', height: '30px', marginLeft: '0.25rem' }}
          />
          {texts.buttonText}
        </button>
      </div>

      {/* Size Guide Modal */}
      {openDialog &&
        createPortal(
          <div
            className="alfinder-size-guide-modal"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2147483647,
              opacity: isClosing ? 0 : 1,
              transition: 'opacity 0.3s ease',
              isolation: 'isolate',
              backdropFilter: 'blur(2px)',
            }}
          >
            <OutsideClickHandler onOutsideClick={handleClose}>
              <div
                style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '32px',
                  maxWidth: '95vw',
                  maxHeight: '95vh',
                  width: '800px',
                  minHeight: '450px',
                  overflow: 'auto',
                  boxShadow:
                    '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                  transform: isClosing ? 'scale(0.95)' : 'scale(1)',
                  transition: 'transform 0.3s ease',
                  direction: isArabic ? 'rtl' : 'ltr',
                  minWidth: '400px',
                  position: 'relative',
                  zIndex: 1000000,
                }}
              >
                {/* Modal Header */}
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '24px',
                    borderBottom: '1px solid #e5e7eb',
                    paddingBottom: '16px',
                  }}
                >
                  <h2
                    style={{
                      margin: 0,
                      fontSize: '20px',
                      fontWeight: '600',
                      color: primaryColor,
                    }}
                  >
                    {texts.modalTitle}
                  </h2>

                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    {/* Unit Conversion Button */}
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '12px',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setIsConverted(true)}
                        style={{
                          color: isConverted
                            ? 'white'
                            : primaryColor || '#000000',
                          backgroundColor: isConverted
                            ? primaryColor || '#000000'
                            : 'white',
                          border: `1px solid ${primaryColor || '#000000'}`,
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: '500',
                          transition: 'opacity 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '0.9';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                        title={
                          isArabic ? 'تحويل إلى بوصة' : 'Convert to inches'
                        }
                      >
                        {'inches'}
                      </button>
                      <button
                        type="button"
                        onClick={() => setIsConverted(false)}
                        style={{
                          color: !isConverted
                            ? 'white'
                            : primaryColor || '#000000',
                          backgroundColor: !isConverted
                            ? primaryColor || '#000000'
                            : 'white',
                          border: `1px solid ${primaryColor || '#000000'}`,
                          border: '1px solid black',
                          borderRadius: '6px',
                          padding: '6px 12px',
                          fontSize: '12px',
                          cursor: 'pointer',
                          fontWeight: '500',
                          transition: 'opacity 0.2s ease',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.opacity = '0.9';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.opacity = '1';
                        }}
                        title={isArabic ? 'تحويل إلى سم' : 'Convert to cm'}
                      >
                        {'cm'}
                      </button>
                    </div>

                    {/* Close Button */}
                    <button
                      type="button"
                      onClick={handleClose}
                      style={{
                        background: 'none',
                        border: 'none',
                        fontSize: '24px',
                        cursor: 'pointer',
                        color: '#6b7280',
                        padding: '4px',
                        borderRadius: '4px',
                        lineHeight: 1,
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#f3f4f6';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      aria-label="Close modal"
                    >
                      ×
                    </button>
                  </div>
                </div>

                {/* Size Table */}
                {currentSizeData?.tableData?.columns &&
                  currentSizeData?.tableData?.rows && (
                    <div
                      style={{
                        overflowX: 'auto',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                      }}
                    >
                      <table
                        style={{
                          width: '100%',
                          borderCollapse: 'collapse',
                          fontSize: '14px',
                          direction: 'ltr',
                        }}
                      >
                        <thead>
                          <tr
                            style={{
                              backgroundColor: '#f9fafb',
                            }}
                          >
                            {tableColumns.map((column) => (
                              <th
                                key={column.key}
                                style={{
                                  padding: '12px',
                                  textAlign: 'center',
                                  fontWeight: '600',
                                  color: 'white',
                                  borderBottom: '1px solid #e5e7eb',
                                  whiteSpace: 'nowrap',
                                  backgroundColor: primaryColor || '#000000',
                                }}
                              >
                                {column.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {tableRows.map((row, index) => (
                            <tr
                              key={index}
                              style={{
                                backgroundColor:
                                  index % 2 === 0 ? 'white' : '#f9fafb',
                              }}
                            >
                              {tableColumns.map((column) => (
                                <td
                                  key={column.key}
                                  style={{
                                    padding: '12px',
                                    textAlign: 'center',
                                    borderBottom:
                                      index < tableRows.length - 1
                                        ? '1px solid #e5e7eb'
                                        : 'none',
                                    color:
                                      column.key === 'measurement'
                                        ? 'white'
                                        : '#374151',
                                    whiteSpace: 'nowrap',
                                    fontWeight:
                                      column.key === 'measurement'
                                        ? '600'
                                        : '400',
                                    backgroundColor:
                                      column.key === 'measurement'
                                        ? primaryColor || '#000000'
                                        : 'none',
                                  }}
                                >
                                  {row[column.key] || '-'}
                                </td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}

                {/* Fallback Table - Show default data if data structure is incomplete */}
                {(!currentSizeData?.tableData?.columns ||
                  !currentSizeData?.tableData?.rows) && (
                  <div
                    style={{
                      overflowX: 'auto',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                    }}
                  >
                    <table
                      style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: '14px',
                      }}
                    >
                      <thead>
                        <tr
                          style={{
                            backgroundColor: '#f9fafb',
                          }}
                        >
                          <th
                            style={{
                              padding: '12px',
                              textAlign: 'center',
                              fontWeight: '600',
                              color: primaryColor || '#000000',
                              borderBottom: '1px solid #e5e7eb',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {isArabic ? 'مقاس' : 'Size'}
                          </th>
                          {defaultSizeData.tableData.rows.map((row, index) => (
                            <th
                              key={index}
                              style={{
                                padding: '12px',
                                textAlign: 'center',
                                fontWeight: '600',
                                color: primaryColor,
                                borderBottom: '1px solid #e5e7eb',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {row.name}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {defaultSizeData.tableData.columns.map(
                          (column, index) => (
                            <tr
                              key={index}
                              style={{
                                backgroundColor:
                                  index % 2 === 0 ? 'white' : '#f9fafb',
                              }}
                            >
                              <td
                                style={{
                                  padding: '12px',
                                  textAlign: 'center',
                                  borderBottom:
                                    index <
                                    defaultSizeData.tableData.columns.length - 1
                                      ? '1px solid #e5e7eb'
                                      : 'none',
                                  color: '#374151',
                                  whiteSpace: 'nowrap',
                                  fontWeight: '600',
                                }}
                              >
                                {column.name} ({column.value})
                              </td>
                              {defaultSizeData.tableData.rows.map(
                                (row, colIndex) => (
                                  <td
                                    key={colIndex}
                                    style={{
                                      padding: '12px',
                                      textAlign: 'center',
                                      borderBottom:
                                        index <
                                        defaultSizeData.tableData.columns
                                          .length -
                                          1
                                          ? '1px solid #e5e7eb'
                                          : 'none',
                                      color: '#374151',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {row.values[index] || '-'}
                                  </td>
                                ),
                              )}
                            </tr>
                          ),
                        )}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Optional Note */}
                <>
                  {currentSizeData?.description && (
                    <div
                      style={{
                        marginTop: '16px',
                        padding: '12px',
                        backgroundColor: '#f0f9ff',
                        borderRadius: '8px',
                        fontSize: '12px',
                        color: '#0369a1',
                        textAlign: 'center',
                      }}
                    >
                      {currentSizeData.description}
                    </div>
                  )}
                </>
              </div>
            </OutsideClickHandler>
          </div>,
          document.body,
        )}
    </>
  );
};

export default SizeGuide;
